import axios from 'axios';
import axiosRetry, { isNetworkError } from 'axios-retry';
import { normalize } from 'normalizr';

import { getBaseUrl } from '../constants';
import { EntityParsers } from '../schemas';
import { authUnset, isLoggingOut } from '../utils';
import resetChatwootSession from '../utils/chatwoot';
import { isEsi } from '../utils/portalType';
import { syncSessionExpiryFromRemainingSeconds, syncSessionTiming } from '../utils/sessionTimeout';
import { isPatient } from '../utils/userRoles';

export const API_CALL = Symbol('api call');
export const API_CANCEL = Symbol('api cancel');

const qs = require('qs');

const axiosInstance = axios.create();

// Workaround for how axios-retry handles CORS errors...
// Based on https://github.com/softonic/axios-retry/issues/138#issuecomment-721632626
const isCorsNetworkError = error =>
  error && !error.response && error.code !== 'ECONNABORTED' && error.message === 'Network Error';

// 429 (rate limit) and 503 (e.g. code-version gate during deploys) reject before the handler
// runs, so every method may retry them. Other failures (500/502, dropped connection) may come
// after the write was committed: only idempotent methods retry those, because resending a
// POST/PATCH could apply it twice. A read-only POST opts in with `idempotent: true`.
const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete'];

export const shouldRetryRequest = error => {
  const status = error?.response?.status;
  if (status === 429 || status === 503) {
    return true;
  }
  const method = (error?.config?.method || 'get').toLowerCase();
  if (!IDEMPOTENT_METHODS.includes(method) && !error?.config?.idempotent) {
    return false;
  }
  // 500 too, because Google Cloud itself (not only our Cloud Function panic) returns 500 when it has issues... https://stackoverflow.com/q/65303743/950131
  return isNetworkError(error) || isCorsNetworkError(error) || status === 500 || status === 502;
};

axiosRetry(axiosInstance, {
  retries: 3,
  retryCondition: shouldRetryRequest,
  retryDelay: (retryCount, _) => {
    return retryCount === 1 ? 500 : retryCount === 2 ? 2000 : 5000;
  },
});

const callAPI = (endpoint, method, params, data, headers, responseType, signal, idempotent) => {
  const req = axiosInstance.request({
    idempotent,
    method,
    baseURL: getBaseUrl(),
    url: endpoint,
    responseType: responseType || undefined,
    params,
    data,
    headers,
    paramsSerializer: parameters => qs.stringify(parameters, { arrayFormat: 'repeat' }),
    signal,
  });

  return req.then(response => response);
};

// Requests started with `latest` that are still running, by key. A list fetch answers
// "what should be shown now": when a newer request for the same key starts (tab switch,
// new filter, refetch after a save), the older one is aborted. Otherwise its response
// could arrive last and overwrite the newer data. The aborted request resolves as
// `${type}/canceled`, which reducers and `resp?.type === X_RESULT` checks already ignore,
// and the server stops working on it.
const latestRequests = new Map();
let latestRequestSeq = 0;

// Every action of a `latest` request carries `latestRequestId`; reducers check it with
// `isLatestResult` (middleware/latest.js).

const startLatestRequest = (key, callerSignal) => {
  latestRequests.get(key)?.abort();
  const controller = new AbortController();
  latestRequests.set(key, controller);
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  return controller;
};

const finishLatestRequest = (key, controller) => {
  if (latestRequests.get(key) === controller) {
    latestRequests.delete(key);
  }
};

const isCanceled = error => axios.isCancel(error) || error?.message === 'canceled';

// For `.then(resp => ...)` callers that treat any non-result as "empty": a superseded
// request carries no data, and the newer request will deliver the real answer.
export const isCanceledResponse = resp => typeof resp?.type === 'string' && resp.type.endsWith('/canceled');

export default store => next => action => {
  const apiCancel = action[API_CANCEL];
  if (typeof apiCancel !== 'undefined') {
    const { controller, latest: latestKeyToCancel } = apiCancel;
    controller?.abort();
    // Clearing a slot also abandons its pending `latest` fetch, so it can't refill the slot.
    if (latestKeyToCancel) {
      latestRequests.get(latestKeyToCancel)?.abort();
    }
  }

  const apiCall = action[API_CALL];
  if (typeof apiCall === 'undefined') {
    return next(action);
  }

  const { auth } = store.getState();

  const { type } = action;
  let { endpoint } = apiCall;
  const {
    method = 'get',
    params,
    data = null,
    formData = null,
    schema,
    callBefore = [],
    callAfterSuccess = [],
    callAfterError = [],
    responseType = undefined,
    signal,
    latest,
    idempotent = false,
  } = apiCall;

  // `latest: true` keys by action type; a string key lets several action types share one slot.
  const latestKey = latest === true ? type : latest;
  const latestController = latestKey ? startLatestRequest(latestKey, signal) : null;
  const requestSignal = latestController ? latestController.signal : signal;
  if (latestController) {
    latestRequestSeq += 1;
  }
  const latestRequestId = latestController ? latestRequestSeq : undefined;

  // Keep compatibility with legacy actions that pass payload as "formData".
  const requestData = data ?? formData;
  const isFormDataPayload = typeof FormData !== 'undefined' && requestData instanceof FormData;

  if (typeof endpoint === 'function') {
    endpoint = endpoint(store.getState());
  }

  const actionWith = actionData => {
    const finalAction = {
      ...action,
      ...(latestRequestId !== undefined ? { latestRequestId } : {}),
      ...actionData,
    };
    delete finalAction[apiCall];
    return finalAction;
  };

  next(actionWith({ type }));

  const headers = auth.authenticated
    ? {
        Authorization: `Token ${auth.token}`,
        'X-Client-Type': 'web',
        Accept: 'application/json',
        ...(isFormDataPayload ? {} : { 'Content-Type': 'application/json' }),
      }
    : {
        'X-Client-Type': 'web',
        Accept: 'application/json',
        ...(isFormDataPayload ? {} : { 'Content-Type': 'application/json' }),
      };

  if (callBefore && Array.isArray(callBefore)) {
    callBefore.forEach(item => store.dispatch(item));
  }

  let respHeaders;

  return callAPI(endpoint, method, params, requestData, headers, responseType, requestSignal, idempotent)
    .then(response => {
      respHeaders = response.headers;
      return (schema && normalize(response.data, schema)) || response.data;
    })
    .then(response => {
      let newResponse = response;
      if (response.entities) {
        const entities = Object.keys(response.entities).reduce((result, key) => {
          const newResult = result;
          const value = response.entities[key];
          newResult[key] = (EntityParsers[key] && EntityParsers[key](value)) || value;
          return newResult;
        }, {});
        newResponse = {
          ...response,
          entities: {
            ...response.entities,
            ...entities,
          },
        };
      }

      return newResponse;
    })
    .then(response => {
      if (response?.remaining_seconds !== undefined) {
        syncSessionExpiryFromRemainingSeconds(response.remaining_seconds);
      } else if (response?.authentication_token || auth.authenticated) {
        syncSessionTiming(response?.org_config);
      }

      if (callAfterSuccess && Array.isArray(callAfterSuccess)) {
        callAfterSuccess.forEach(item => store.dispatch(item));
      }
      return next(
        actionWith({
          type: [type, 'result'].join('/'),
          headers: respHeaders,
          response,
        }),
      );
    })
    .catch(error => {
      // A cancelled request is not a failure: no session handling, no error side effects.
      if (isCanceled(error)) {
        return next(
          actionWith({
            type: [type, 'canceled'].join('/'),
          }),
        );
      }

      if (error.response && error.response.status === 401) {
        const isUserPatient = isPatient();
        const isUserEsi = isEsi();

        resetChatwootSession();
        authUnset();

        // While the user explicitly signs out, requests that were in flight when the
        // session was invalidated answer 401. Redirecting here would abort the sign-out
        // navigation to /login and land on "Session Expired" instead, so skip it.
        if (!isLoggingOut() && window.location.pathname.indexOf('/login') !== 0) {
          const sessionExpiredPath = isUserPatient && isUserEsi ? '/session-expired?esi=1' : '/session-expired';
          window.location.href = sessionExpiredPath;
        }
      }

      if (error.response && error.response.status === 403) {
        return next(
          actionWith({
            type: ['auth/status', 'error'].join('/'),
            error: error.message || 'Something bad happened',
            response: error.response,
          }),
        );
      }

      if (callAfterError && Array.isArray(callAfterError)) {
        callAfterError.forEach(item => store.dispatch(item));
      }
      return next(
        actionWith({
          type: [type, 'error'].join('/'),
          error: error.message || 'Something bad happened',
          response: error.response,
        }),
      );
    })
    .finally(() => {
      if (latestController) {
        finishLatestRequest(latestKey, latestController);
      }
    });
};
